import { api } from './api';
import type {
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  LoginResponse,
  RegisterResponse,
  RefreshResponse,
  LogoutResponse,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  AuthSuccessResponse,
  RegistrationSuccessResponse
} from '../types/auth';
import { User } from '../stores/authStore';

class AuthService {
  private refreshTokenKey = 'refresh-token';
  private accessTokenKey = 'access-token';

  /**
   * Convert user role to lowercase for frontend compatibility
   */
  private normalizeUserRole(user: any): User {
    return {
      ...user,
      role: user.role ? (user.role as string).toLowerCase() as 'admin' | 'organizer' | 'member' | 'finance_manager' : 'member'
    };
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthSuccessResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      
      if (!response.success || !response.data) {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message;
        throw new Error(errorMsg || 'Login failed');
      }

      // Store tokens
      this.storeTokens(response.data.accessToken, response.data.refreshToken);

      // Normalize user role to lowercase
      const normalizedResponse = {
        ...response.data,
        user: this.normalizeUserRole(response.data.user)
      };

      return normalizedResponse;
    } catch (error) {
      this.handleAuthError(error);
      
      // If it's an axios error, extract the proper error message from the response
      if (error && typeof error === 'object' && 'response' in error && error.response) {
        const response = error.response as any;
        if (response.data?.error?.message) {
          const customError = new Error(response.data.error.message) as any;
          customError.response = error.response;
          throw customError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Register new user (creates unverified account pending admin approval)
   */
  async register(userData: RegisterRequest): Promise<RegistrationSuccessResponse> {
    try {
      const response = await api.post<RegisterResponse>('/auth/register', userData);
      
      if (!response.success || !response.data) {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message;
        throw new Error(errorMsg || 'Registration failed');
      }

      // Registration successful - no tokens stored since user needs admin verification
      return response.data;
    } catch (error) {
      this.handleAuthError(error);
      
      // If it's an axios error, extract the proper error message from the response
      if (error && typeof error === 'object' && 'response' in error && error.response) {
        const response = error.response as any;
        if (response.data?.error?.message) {
          const customError = new Error(response.data.error.message) as any;
          customError.response = error.response;
          throw customError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (refreshToken) {
        // Call logout endpoint to invalidate refresh token on server
        await api.post<LogoutResponse>('/auth/logout', { refreshToken });
      }
    } catch (error) {
      // Even if server logout fails, we should still clear local tokens
      console.error('Server logout failed:', error);
    } finally {
      // Always clear local tokens
      this.clearTokens();
    }
  }

  /**
   * Refresh access token (uses direct axios call to avoid interceptor loops)
   */
  async refreshAccessToken(): Promise<string | null> {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Use direct axios call to avoid interceptor loops
      const { default: axios } = await import('axios');
      const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3083').replace(/\/$/, '');
      
      const response = await axios.post<RefreshResponse>(`${API_BASE_URL}/auth/refresh`, {
        refreshToken
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000
      });

      const data = response.data;
      if (!data.success || !data.data) {
        const errorMsg = typeof data.error === 'string'
          ? data.error
          : data.error?.message;
        throw new Error(errorMsg || 'Token refresh failed');
      }

      // Update stored access token
      this.storeAccessToken(data.data.accessToken);
      
      // Update refresh token if provided
      if (data.data.refreshToken) {
        this.storeRefreshToken(data.data.refreshToken);
      }

      return data.data.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Don't call authHandler.handleAuthFailure here to avoid circular dependency
      // The API interceptor will handle the failure
      return null;
    }
  }

  /**
   * Request password reset email
   */
  async forgotPassword(request: ForgotPasswordRequest): Promise<void> {
    try {
      const response = await api.post<ForgotPasswordResponse>('/auth/forgot-password', request);
      
      if (!response.success) {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message;
        throw new Error(errorMsg || 'Failed to send password reset email');
      }
    } catch (error) {
      // If it's an axios error, extract the proper error message from the response
      if (error && typeof error === 'object' && 'response' in error && error.response) {
        const response = error.response as any;
        if (response.data?.error?.message) {
          const customError = new Error(response.data.error.message) as any;
          customError.response = error.response;
          throw customError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(request: ResetPasswordRequest): Promise<void> {
    try {
      const response = await api.post<ResetPasswordResponse>('/auth/reset-password', request);
      
      if (!response.success) {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message;
        throw new Error(errorMsg || 'Failed to reset password');
      }
    } catch (error) {
      // If it's an axios error, extract the proper error message from the response
      if (error && typeof error === 'object' && 'response' in error && error.response) {
        const response = error.response as any;
        if (response.data?.error?.message) {
          const customError = new Error(response.data.error.message) as any;
          customError.response = error.response;
          throw customError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    
    return !!(accessToken && refreshToken);
  }

  /**
   * Store both access and refresh tokens
   */
  private storeTokens(accessToken: string, refreshToken: string): void {
    this.storeAccessToken(accessToken);
    this.storeRefreshToken(refreshToken);
  }

  /**
   * Store access token
   */
  private storeAccessToken(accessToken: string): void {
    localStorage.setItem(this.accessTokenKey, accessToken);
    // Also update the legacy authToken for backward compatibility with api interceptor
    localStorage.setItem('authToken', accessToken);
  }

  /**
   * Store refresh token
   */
  private storeRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  /**
   * Clear all stored tokens
   */
  private clearTokens(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem('authToken'); // Legacy token
  }

  /**
   * Clear all auth-related data from storage (including Zustand persisted state)
   */
  clearAllAuthData(): void {
    this.clearTokens();
    localStorage.removeItem('auth-storage'); // Zustand persisted auth state
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: unknown): void {
    // If it's an unauthorized error, clear tokens
    if (this.isUnauthorizedError(error)) {
      this.clearTokens();
    }
    
    // Log more detailed error information for debugging
    if (error && typeof error === 'object') {
      console.error('Auth error details:', {
        message: 'message' in error ? error.message : 'Unknown error',
        code: 'code' in error ? error.code : 'No code',
        status: 'response' in error && error.response && typeof error.response === 'object' && 'status' in error.response ? error.response.status : 'No status'
      });
    }
  }

  /**
   * Check if error is unauthorized (401)
   */
  private isUnauthorizedError(error: unknown): boolean {
    return !!(
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response &&
      error.response.status === 401
    );
  }

  /**
   * Parse JWT payload (without verification - for client-side info only)
   */
  parseTokenPayload(token: string): Record<string, unknown> | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }

  /**
   * Check if token is expired (client-side check only)
   */
  isTokenExpired(token: string): boolean {
    const payload = this.parseTokenPayload(token);
    if (!payload || !payload['exp']) {
      return true;
    }
    
    const expirationTime = (payload['exp'] as number) * 1000; // Convert to milliseconds
    return Date.now() >= expirationTime;
  }

  /**
   * Get user info from access token (client-side only)
   */
  getUserFromToken(): User | null {
    const token = this.getAccessToken();
    if (!token) return null;

    const payload = this.parseTokenPayload(token);
    if (!payload) return null;

    try {
      const rawRole = payload['role'] as string;
      const firstName = payload['firstName'] as string || payload['first_name'] as string || '';
      const lastName = payload['lastName'] as string || payload['last_name'] as string || '';
      const middleName = payload['middleName'] as string || payload['middle_name'] as string || null;
      const fullName = payload['name'] as string || `${firstName} ${lastName}`.trim();
      
      return {
        id: payload['sub'] as string,
        firstName,
        lastName,
        middleName,
        name: fullName,
        email: payload['email'] as string,
        role: rawRole ? rawRole.toLowerCase() as 'admin' | 'organizer' | 'member' | 'finance_manager' : 'member',
        createdAt: payload['iat'] && typeof payload['iat'] === 'number' ? new Date(payload['iat'] * 1000).toISOString() : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;